import {Product, WishlistDataContainer} from '../../@types/wishlist.js';
import {CookieHandler, CookieHandlerInstance} from './cookiehandler.js';
import {
  ADD_TO_WISHLIST_EVENT,
  ADDED_TO_WISHLIST_EVENT,
  config,
  LOGGED_OUT_EVENT,
  REMOVE_FROM_WISHLIST_EVENT,
  REMOVED_FROM_WISHLIST_EVENT,
} from './config.js';
import {WishlistApi, WishlistApiInstance} from './wishlist-api.js';
import { trackAddToWishlist, trackRemoveFromWishlist } from './cube-tracking-layer.js';
import {track} from './generic-tracking-layer.js';
import {SortAction} from '../filter/sort-action.js';

export const LAST_LOGIN_STATE_COOKIE = 'ents_wl_last_login_state';
export const COOKIE_EXPIRATION_DAYS = 360;

export class WishlistService {
  private readonly api: WishlistApi;

  private readonly cookieHandler: CookieHandler;

  private declare readonly onAddToWishlist: (_: CustomEvent<WishlistDataContainer>) => void;

  private declare readonly onRemoveFromWishlist: (_: CustomEvent<WishlistDataContainer>) => void;

  constructor(api: WishlistApi, cookieHandler: CookieHandler) {
    this.api = api;
    this.cookieHandler = cookieHandler;
    this.onAddToWishlist = (e: Event) => {
      this.addToWishlist((e as CustomEvent).detail);
    };
    this.onRemoveFromWishlist = (e) => {
      const data = (e as CustomEvent).detail;
      const trackingCallback = () => trackRemoveFromWishlist(data.syan, data.colorId, data.articleId, data.tracking);
      this.removeFromWishlist(data, trackingCallback);
    };
  }

  init() {
    this.destroy();
    document.addEventListener(ADD_TO_WISHLIST_EVENT, this.onAddToWishlist);
    document.addEventListener(REMOVE_FROM_WISHLIST_EVENT, this.onRemoveFromWishlist);
    this.handleCookiesForMergeDetection();
  }

  destroy() {
    document.removeEventListener(ADD_TO_WISHLIST_EVENT, this.onAddToWishlist);
    document.removeEventListener(REMOVE_FROM_WISHLIST_EVENT, this.onRemoveFromWishlist);
  }

  handleCookiesForMergeDetection() {
    const lastLoginState = this.cookieHandler.getCookie(LAST_LOGIN_STATE_COOKIE);
    const userIsLoggedIn = config.currentlyLoggedIn === 'true';
    if (userIsLoggedIn) {
      if (WishlistService.userWasLoggedOutBefore(lastLoginState)) {
        this.api.merge();
      }
      this.cookieHandler.setCookie(LAST_LOGIN_STATE_COOKIE, 'true', COOKIE_EXPIRATION_DAYS);
    } else {
      if (WishlistService.userWasLoggedInBefore(lastLoginState)) {
        document.dispatchEvent(new Event(LOGGED_OUT_EVENT));
      }
      this.cookieHandler.setCookie(LAST_LOGIN_STATE_COOKIE, 'false', COOKIE_EXPIRATION_DAYS);
    }
  }

  static getProductsWithCollectionStatusSortedByDefaultSorting(
    products: Product[],
    wishlistCollectionId?: string
  ): (Product & { isInCollection: boolean })[] {
    if (!wishlistCollectionId) {
      return products.map(p => ({...p, isInCollection: false}));
    }
    return products
      .map(
        (product) => {
          const isInCollection = product.collectionReferences.map(
            cr => cr.collectionId
          ).includes(wishlistCollectionId);
          return {
            ...product,
            isInCollection,
          };
        }
      );
  }

  static getProductsForCollectionWithDefaultSorting(
    products: Product[],
    wishlistCollectionId: string
  ): Product[] {
    return products
      .filter(p => p.collectionReferences.map(r => r.collectionId).includes(wishlistCollectionId))
      .map((p) => {
        const collectionReference = p.collectionReferences.find(r => r.collectionId === wishlistCollectionId);
        return {
          ...p,
          sortId: -1 * (collectionReference?.sortId ?? 0),
        };
      })
      .sort(SortAction.createRecentlyAddedSortStrategy().compareStrategy);
  }

  static userWasLoggedOutBefore(lastLoginState: string) {
    return lastLoginState === 'false';
  }

  static userWasLoggedInBefore(lastLoginState: string) {
    return lastLoginState === 'true';
  }

  static trackGenericAddToWishlist() {
    track({event: 'product.wishlistAdd'});
  }

  async addToWishlist(data: WishlistDataContainer) {
    // optimistic update
    WishlistService.dispatchAddedEvent(data);
    const result = await this.api.add({
      syan: data.syan,
      colorId: data.colorId,
      articleId: data.articleId,
    });
    if (result) {
      document.dispatchEvent(new Event('ents:mobileapp:wishlist:added'));
      trackAddToWishlist(data);
      WishlistService.trackGenericAddToWishlist();
    } else {
      // roll back
      WishlistService.dispatchRemovedEvent(data);
    }
  }

  async changeItemOnWishlist(syan: number, colorId?: string, articleId?: string) {
    return this.api.add({
      syan,
      colorId,
      articleId,
    });
  }

  async removeFromWishlist(data: WishlistDataContainer, trackingCallback: () => void) {
    // optimistic update
    WishlistService.dispatchRemovedEvent(data);
    const result = await this.api.remove({
      syan: data.syan,
      colorId: data.colorId,
    });
    if (result) {
      document.dispatchEvent(new Event('ents:mobileapp:wishlist:removed'));
      trackingCallback();
    } else {
      // roll back
      WishlistService.dispatchAddedEvent(data);
    }
    return result;
  }

  private static dispatchAddedEvent(data: WishlistDataContainer) {
    document.dispatchEvent(new CustomEvent(ADDED_TO_WISHLIST_EVENT, {detail: data}));
  }

  private static dispatchRemovedEvent(data: WishlistDataContainer) {
    document.dispatchEvent(new CustomEvent(REMOVED_FROM_WISHLIST_EVENT, {detail: data}));
  }
}

export const wishlistService = new WishlistService(WishlistApiInstance, CookieHandlerInstance);
